/**
 * JourneyRunner — expérience Academy conversationnelle 10 étapes.
 *
 * UX: bulles de dialogue du formateur Hélène + révélation progressive des
 * blocs KMS + carte MapEmbed injectée aux étapes pédagogiquement pertinentes.
 * Phase 2: feedback DeepSeek sur réponses libres pour l'étape cas_pratique.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, auth } from "@/lib/api";
import { useT } from "@/lib/i18n";
import MapEmbed from "@/components/MapEmbed";
import AppellationFactsCard from "@/components/AppellationFactsCard";
import { renderMarkdown } from "@/lib/markdown";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import {
  ArrowLeft, Sparkles, ChevronRight, Loader2, Award, Trophy,
  CheckCircle2, Circle, AlertCircle, Send, RefreshCw,
} from "lucide-react";

const LEVEL_LABELS = {
  decouverte: { label: "Découverte", color: "#8B5CF6", emoji: "🌱" },
  comprehension: { label: "En compréhension", color: "#F59E0B", emoji: "💡" },
  maitrise: { label: "Maîtrise", color: "#10B981", emoji: "🏆" },
};

export default function JourneyRunner() {
  const { slug } = useParams();
  const { lang } = useT();
  const [journey, setJourney] = useState(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [revealed, setRevealed] = useState(1); // number of items revealed in current step
  const [done, setDone] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [sending, setSending] = useState(false);
  const [answer, setAnswer] = useState("");
  const [mastery, setMastery] = useState(null);
  const [levelPicker, setLevelPicker] = useState(false);
  const [askedAnswers, setAskedAnswers] = useState({});
  const [freeTurns, setFreeTurns] = useState([]); // user/ai turns from free input
  const [freeInput, setFreeInput] = useState("");
  const [freeBusy, setFreeBusy] = useState(false);
  const [narrationCache, setNarrationCache] = useState({}); // {stepKey: [bubbles]}
  const [narrationLoading, setNarrationLoading] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const scrollRef = useRef(null);
  const loggedIn = !!auth.getToken();

  // Pre-written introductions for KMS blocks, so geoVINUM IA stays in character.
  const BLOCK_INTROS = [
    "Première pépite :",
    "Dans la foulée :",
    "Pour aller plus loin :",
    "Un détail qui change tout :",
    "Autre point crucial :",
    "Encore une facette :",
    "Dernier point marquant :",
    "Et pour finir cette étape :",
  ];

  // Build the ordered stream of items a learner must traverse within a step.
  // Narration + asks come first (declared order), then the map, then blocks.
  const buildStream = (step) => {
    if (!step) return [];
    const stream = [];
    // Prefer the dynamically generated narration (DeepSeek, KMS-grounded).
    // Keyed by (stepKey:level:lang) so level/lang switches auto-invalidate.
    const effectiveLevel = journey?.level || "intermediaire";
    const cacheKey = `${step.key}:${effectiveLevel}:${lang}`;
    const dynamic = narrationCache[cacheKey];
    const narration = (dynamic && dynamic.length > 0) ? dynamic : (step.narration || []);
    narration.forEach((n) =>
      stream.push(n.role === "ask"
        ? { kind: "ask", text: n.text, options: n.options || [] }
        : { kind: "guide", text: n.text }));
    if (step.map) stream.push({ kind: "map" });
    (step.blocks || []).forEach((b, i) => {
      stream.push({ kind: "guide", text: BLOCK_INTROS[i % BLOCK_INTROS.length] });
      stream.push({ kind: "block", block: b });
    });
    if (step.open_prompt) stream.push({ kind: "prompt" });
    return stream;
  };

  // Load journey (optionally with explicit level)
  const loadJourney = (lvl) => {
    const url = lvl
      ? `/academy/courses/${slug}/journey?level=${lvl}`
      : `/academy/courses/${slug}/journey`;
    return api.get(url).then((r) => {
      setJourney(r.data);
      if (r.data?.progress?.current_step) {
        const idx = (r.data.steps || []).findIndex(
          (s) => s.key === r.data.progress.current_step,
        );
        if (idx > 0) setStepIdx(idx);
      }
      if (!lvl && !r.data?.progress?.level) {
        setLevelPicker(true);
      }
    });
  };

  useEffect(() => {
    // Full state reset when navigating to a different course so the new
    // journey does not inherit stale state (bulles, étape, mastery, quiz…)
    // from a previously viewed course.
    setJourney(null);
    setStepIdx(0);
    setRevealed(1);
    setDone(false);
    setFeedback(null);
    setSending(false);
    setAnswer("");
    setMastery(null);
    setLevelPicker(false);
    setAskedAnswers({});
    setFreeTurns([]);
    setFreeInput("");
    setFreeBusy(false);
    setNarrationCache({});
    setNarrationLoading(false);
    setShowQuiz(false);
    loadJourney().catch(() => toast.error("Voyage indisponible"));
    // eslint-disable-next-line
  }, [slug]);

  const pickLevel = async (lvl) => {
    setLevelPicker(false);
    setStepIdx(0); setRevealed(1); setFeedback(null); setAnswer(""); setAskedAnswers({});
    // Note: narrationCache keys embed level/lang so no need to clear here.
    try {
      await loadJourney(lvl);
      toast.success(`Niveau choisi : ${lvl === "decouverte" ? "Découverte" : lvl === "expert" ? "Expert" : "Intermédiaire"}`);
    } catch { toast.error("Impossible de recharger le voyage"); }
  };

  const steps = journey?.steps || [];
  const step = steps[stepIdx];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stream = useMemo(() => buildStream(step), [step, narrationCache]);
  const visibleStream = stream.slice(0, revealed);
  const streamDone = revealed >= stream.length;
  const pendingAsk = visibleStream.find((it) => it.kind === "ask" && askedAnswers[`${stepIdx}-${visibleStream.indexOf(it)}`] === undefined);

  // Fetch dynamic step narration whenever the user lands on a new step.
  // Cache key = `${stepKey}:${level}:${lang}` so a level or language switch
  // automatically refetches without wiping other entries.
  useEffect(() => {
    if (!journey || !step?.key) return;
    const effectiveLevel = journey.level || "intermediaire";
    const cacheKey = `${step.key}:${effectiveLevel}:${lang}`;
    if (narrationCache[cacheKey]) return;
    const stepKey = step.key;
    setNarrationLoading(true);
    const ctrl = new AbortController();
    api.get(`/academy/courses/${slug}/journey/narrate`, {
      params: { step: stepKey, lang, level: effectiveLevel },
      signal: ctrl.signal,
    })
      .then((r) => {
        const bubbles = r.data?.bubbles || [];
        if (bubbles.length > 0) {
          setNarrationCache((c) => ({ ...c, [cacheKey]: bubbles }));
          setRevealed(1); // reveal first bubble of the fresh narration
        }
      })
      .catch(() => { /* silent: keep static fallback */ })
      .finally(() => setNarrationLoading(false));
    return () => ctrl.abort();
    // eslint-disable-next-line
  }, [step?.key, journey?.level, lang]);

  const nextStep = () => {
    const next = Math.min(stepIdx + 1, steps.length - 1);
    setStepIdx(next);
    setRevealed(1);
    setFeedback(null);
    setAnswer("");
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
    if (loggedIn) {
      api.post(`/academy/courses/${slug}/journey/progress`,
        { step_key: steps[next]?.key, completed: true }).catch(() => {});
    }
    if (next === steps.length - 1 && step?.key === "cas_pratique") {
      finishJourney();
    }
  };

  const revealNext = () => {
    if (pendingAsk) return; // user must answer first
    setRevealed((r) => Math.min(r + 1, stream.length));
    setTimeout(() => {
      scrollRef.current?.scrollTo?.({ top: 9e9, behavior: "smooth" });
    }, 50);
  };

  const answerAsk = (streamIdx, option) => {
    setAskedAnswers((prev) => ({ ...prev, [`${stepIdx}-${streamIdx}`]: option }));
    setTimeout(() => setRevealed((r) => Math.min(r + 1, stream.length)), 300);
  };

  const finishJourney = async () => {
    // Open the quiz interstitial first — the recap page will be reached
    // after quiz submission (or if the user explicitly skips).
    setShowQuiz(true);
  };

  const onQuizCompleted = async () => {
    if (loggedIn) {
      try {
        const r = await api.get(`/academy/courses/${slug}/journey/mastery`);
        setMastery(r.data);
      } catch { /* ignore */ }
    }
    setShowQuiz(false);
    setDone(true);
  };

  const submitAnswer = async () => {
    if (!answer.trim() || answer.trim().length < 4) {
      toast.error("Développez un peu votre réponse"); return;
    }
    setSending(true); setFeedback(null);
    try {
      const r = await api.post(
        `/academy/courses/${slug}/journey/analyze`,
        { step_key: step.key, user_answer: answer.trim(), lang },
      );
      setFeedback(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "L'IA n'a pas répondu");
    } finally { setSending(false); }
  };

  const askFree = async () => {
    const q = freeInput.trim();
    if (q.length < 2) return;
    setFreeInput("");
    setFreeTurns((t) => [...t, { role: "user", text: q }]);
    setFreeBusy(true);
    try {
      const r = await api.post(`/academy/courses/${slug}/journey/ask`, {
        step_key: step?.key,
        question: q,
        lang,
      });
      setFreeTurns((t) => [...t, { role: "ai", text: r.data?.reply || "…" }]);
    } catch (e) {
      setFreeTurns((t) => [...t, {
        role: "ai",
        text: "Désolé, je n'ai pas pu répondre à cette question. Reformulez ?",
      }]);
    } finally { setFreeBusy(false); }
    setTimeout(() => scrollRef.current?.scrollTo?.({ top: 9e9, behavior: "smooth" }), 80);
  };

  if (!journey) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-neutral-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Préparation du voyage…
      </div>
    );
  }

  if (showQuiz) {
    return (
      <JourneyQuiz
        slug={slug}
        lang={lang}
        level={journey.level || "intermediaire"}
        appellationName={journey.appellation_name}
        onDone={onQuizCompleted}
      />
    );
  }

  if (done) {
    return <JourneyRecap journey={journey} mastery={mastery} slug={slug} />;
  }

  const isLastStep = stepIdx === steps.length - 1;

  return (
    <div className="min-h-[calc(100dvh_-_var(--m-header-h)_-_var(--m-tabs-h))] md:min-h-screen bg-gradient-to-b from-[#FDF9F3] via-white to-[#F5EDE2]">
      {/* Level picker overlay */}
      {levelPicker && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 overlay-safe"
             data-testid="level-picker">
          <div className="bg-white rounded-2xl max-w-md w-full p-5 shadow-xl">
            <h2 className="font-display text-xl text-[#1F3A5F]">
              Quel est votre niveau sur {journey.appellation_name} ?
            </h2>
            <p className="text-xs text-neutral-500 mt-1">
              geoVINUM IA adaptera la narration et la profondeur des contenus.
            </p>
            <div className="mt-4 space-y-2">
              {journey.levels.map((lv) => (
                <button key={lv.key}
                        onClick={() => pickLevel(lv.key)}
                        data-testid={`level-${lv.key}`}
                        className="w-full flex items-start gap-3 bg-neutral-50 hover:bg-[#FBF7F3] border border-neutral-200 hover:border-[#E5276A] rounded-xl p-3 text-left transition">
                  <span className="text-2xl">{lv.emoji}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-semibold text-[#1F3A5F] text-sm">{lv.label}</span>
                    <span className="block text-xs text-neutral-500 mt-0.5">{lv.hint}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Topbar progress */}
      <div className="sticky top-[var(--m-header-h)] z-20 bg-white/90 backdrop-blur border-b border-neutral-200">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/academy" className="text-neutral-500 hover:text-[#E5276A]" data-testid="journey-back">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-widest text-[#E5276A] font-semibold flex items-center gap-2">
              <span>Voyage · {journey.appellation_name}</span>
              {journey.level && (
                <button type="button" onClick={() => setLevelPicker(true)}
                        data-testid="journey-change-level"
                        className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#FCE4ED] text-[#E5276A] font-bold normal-case tracking-normal hover:bg-[#E5276A] hover:text-white transition">
                  {journey.levels?.find((l) => l.key === journey.level)?.emoji || "🍇"}{" "}
                  {journey.levels?.find((l) => l.key === journey.level)?.label || "Niveau"}
                </button>
              )}
            </div>
            <div className="h-1.5 bg-neutral-100 rounded-full mt-1 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#E5276A] to-[#1F3A5F] transition-all"
                   style={{ width: `${((stepIdx + 1) / steps.length) * 100}%` }} />
            </div>
          </div>
          <div className="text-xs font-medium text-neutral-500">
            {stepIdx + 1} / {steps.length}
          </div>
        </div>
        {/* Step stepper */}
        <div className="max-w-3xl mx-auto px-4 pb-2 overflow-x-auto">
          <div className="flex gap-1 min-w-max">
            {steps.map((s, i) => (
              <button key={s.key}
                      onClick={() => i <= stepIdx && setStepIdx(i)}
                      data-testid={`journey-step-${s.key}`}
                      className={`text-[10px] px-2 py-1 rounded-full whitespace-nowrap ${
                        i === stepIdx
                          ? "bg-[#E5276A] text-white font-semibold"
                          : i < stepIdx
                            ? "bg-[#1F3A5F] text-white"
                            : "bg-neutral-100 text-neutral-500"
                      }`}>
                {s.icon} {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div ref={scrollRef} className="max-w-3xl mx-auto px-4 py-6 pb-32">
        <div className="mb-2 text-[11px] uppercase tracking-widest text-[#1F3A5F] font-semibold">
          Étape {stepIdx + 1} · {step.icon}
        </div>
        <h1 className="text-2xl md:text-3xl font-display text-[#1F3A5F]" data-testid="journey-step-title">
          {step.label}
        </h1>

        {/* Dialogue bubbles */}
        {/* Unified conversational stream */}
        <div className="mt-6 space-y-3" data-testid="journey-dialogue">
          {narrationLoading && !narrationCache[`${step.key}:${journey?.level || "intermediaire"}:${lang}`] && (
            <div className="flex gap-3 items-center text-xs text-neutral-500" data-testid="narration-loading">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              geoVINUM IA prépare un contenu adapté à cette étape…
            </div>
          )}
          {visibleStream.map((item, i) => (
            <StreamItem key={`${stepIdx}-${i}`}
                        item={item}
                        guide={journey.guide}
                        appellationSlug={journey.appellation_slug}
                        answered={askedAnswers[`${stepIdx}-${i}`]}
                        onAnswer={(opt) => answerAsk(i, opt)} />
          ))}
        </div>

        {!streamDone && !pendingAsk && (
          <Button onClick={revealNext}
                  data-testid="journey-next-bubble"
                  variant="outline"
                  className="mt-4 text-xs">
            Suite… <ChevronRight className="w-3 h-3 ml-1" />
          </Button>
        )}

        {/* Free-text turns asked by the learner mid-journey */}
        {freeTurns.length > 0 && (
          <div className="mt-6 space-y-3" data-testid="journey-free-turns">
            {freeTurns.map((t, i) => (
              t.role === "user" ? (
                <div key={i} className="flex justify-end">
                  <div className="max-w-[85%] bg-[#1F3A5F] text-white rounded-2xl rounded-tr-sm px-3 py-2 text-sm">
                    {t.text}
                  </div>
                </div>
              ) : (
                <GuideBubble key={i} guide={journey.guide} text={t.text} />
              )
            ))}
            {freeBusy && (
              <div className="flex gap-3 items-center text-xs text-neutral-500">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> geoVINUM IA cherche dans le KMS…
              </div>
            )}
          </div>
        )}

        {/* Open prompt (cas_pratique) */}
        {streamDone && step.open_prompt && (
          <Card className="mt-6 border-2 border-[#E5276A]/30 bg-[#FBF7F3]" data-testid="journey-open">
            <CardContent className="p-4">
              <div className="text-[11px] uppercase tracking-widest text-[#E5276A] font-semibold">
                🎯 Mise en situation
              </div>
              <p className="text-sm text-[#1F3A5F] mt-2 font-medium">{step.open_prompt}</p>
              <textarea value={answer}
                        onChange={(e) => setAnswer(e.target.value)}
                        rows={5}
                        data-testid="journey-answer"
                        placeholder="Expliquez librement — geoVINUM IA analysera votre réponse."
                        className="w-full mt-3 border rounded-xl p-3 text-sm" />
              <Button onClick={submitAnswer}
                      disabled={sending}
                      data-testid="journey-submit"
                      className="mt-3 bg-[#E5276A] hover:bg-[#C01661]">
                {sending ? (
                  <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> geoVINUM IA analyse…</>
                ) : (
                  <>Analyser ma réponse <Sparkles className="w-4 h-4 ml-1" /></>
                )}
              </Button>

              {feedback && (
                <div className="mt-5 border-t pt-4 space-y-3" data-testid="journey-feedback">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{LEVEL_LABELS[feedback.level]?.emoji}</span>
                    <div>
                      <div className="text-sm font-semibold" style={{ color: LEVEL_LABELS[feedback.level]?.color }}>
                        {LEVEL_LABELS[feedback.level]?.label} · {feedback.score}/100
                      </div>
                    </div>
                  </div>
                  <GuideBubble guide={journey.guide} text={feedback.guided_reply} />
                  {feedback.praised?.length > 0 && (
                    <div>
                      <div className="text-[11px] uppercase tracking-widest text-emerald-700 font-semibold mb-1">
                        ✅ Ce que vous avez bien saisi
                      </div>
                      <ul className="text-sm space-y-1">
                        {feedback.praised.map((p, i) => (
                          <li key={i} className="text-neutral-700">• {p}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {feedback.to_improve?.length > 0 && (
                    <div>
                      <div className="text-[11px] uppercase tracking-widest text-amber-700 font-semibold mb-1">
                        💡 Pour aller plus loin
                      </div>
                      <ul className="text-sm space-y-1">
                        {feedback.to_improve.map((p, i) => (
                          <li key={i} className="text-neutral-700">• {p}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Continue */}
        {streamDone && (
          <div className="mt-8 flex justify-end">
            {isLastStep ? (
              <Button onClick={finishJourney}
                      data-testid="journey-finish"
                      className="bg-gradient-to-r from-[#E5276A] to-[#1F3A5F] text-white">
                <Trophy className="w-4 h-4 mr-1" /> Valider mon voyage
              </Button>
            ) : (
              <Button onClick={nextStep}
                      data-testid="journey-next-step"
                      disabled={step.open_prompt && !feedback}
                      className="bg-[#1F3A5F] hover:bg-[#17304d]">
                Étape suivante <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Ask-anywhere composer — sits right above the mobile bottom tabs */}
      <div className="fixed bottom-0 pb-[var(--m-tabs-h)] left-0 md:left-[var(--sidebar-w)] right-0 z-30 bg-white/95 backdrop-blur border-t border-neutral-200">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <form onSubmit={(e) => { e.preventDefault(); askFree(); }}
                className="flex items-center gap-2 bg-white rounded-2xl border border-[#1F3A5F]/15 focus-within:border-[#E5276A] px-3 py-2 shadow-sm">
            <input value={freeInput}
                   onChange={(e) => setFreeInput(e.target.value)}
                   data-testid="journey-ask-input"
                   placeholder={{ en: "Ask geoVINUM AI a question…", es: "Haz una pregunta a geoVINUM IA…", pt: "Faz uma pergunta à geoVINUM IA…" }[lang] || "Posez une question à geoVINUM IA…"}
                   className="flex-1 bg-transparent outline-none text-sm text-[#1F3A5F] placeholder:text-[#8B95A1]" />
            <Button type="submit"
                    disabled={freeBusy || !freeInput.trim()}
                    data-testid="journey-ask-send"
                    className="h-9 bg-[#E5276A] hover:bg-[#C01661] shrink-0">
              {freeBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

function StreamItem({ item, guide, appellationSlug, answered, onAnswer }) {
  if (item.kind === "guide") {
    return <GuideBubble guide={guide} text={item.text} />;
  }
  if (item.kind === "map") {
    return (
      <div className="rounded-2xl overflow-hidden" data-testid="journey-map-inline">
        <MapEmbed appellation={appellationSlug} height={360} />
        <div className="mt-2"><AppellationFactsCard slug={appellationSlug} /></div>
      </div>
    );
  }
  if (item.kind === "ask") {
    return (
      <div className="flex gap-3 items-start" data-testid="journey-ask">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#E5276A] to-[#1F3A5F] text-white flex items-center justify-center text-[10px] font-bold shrink-0 shadow-sm">
          gV·IA
        </div>
        <div className="flex-1 bg-white border border-[#1F3A5F]/15 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
          <div className="text-[10px] uppercase tracking-widest text-[#E5276A] font-semibold mb-1">
            {guide?.name || "geoVINUM IA"}
          </div>
          <p className="text-sm text-neutral-700 leading-relaxed">{item.text}</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {(item.options || []).map((opt) => {
              const active = answered === opt;
              return (
                <button key={opt}
                        onClick={() => onAnswer(opt)}
                        disabled={!!answered}
                        data-testid={`ask-opt-${opt.slice(0,20)}`}
                        className={`text-xs px-3 py-1.5 rounded-full border transition ${
                          active
                            ? "bg-[#E5276A] text-white border-[#E5276A]"
                            : answered
                              ? "bg-neutral-100 text-neutral-400 border-neutral-100"
                              : "bg-white text-[#1F3A5F] border-[#1F3A5F]/20 hover:border-[#E5276A] hover:text-[#E5276A]"
                        }`}>
                  {opt}
                </button>
              );
            })}
          </div>
          {answered && (
            <p className="text-[11px] text-neutral-500 italic mt-2">
              Merci pour votre choix — on continue 👇
            </p>
          )}
        </div>
      </div>
    );
  }
  if (item.kind === "block") {
    const b = item.block;
    return (
      <div className="flex gap-3 items-start" data-testid="journey-block">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#1F3A5F] to-[#17304d] text-white flex items-center justify-center text-sm shrink-0 shadow-sm">
          {b.emoji || "📖"}
        </div>
        <div className="flex-1 bg-gradient-to-br from-[#FBF7F3] to-white border border-[#E5276A]/10 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
          <h3 className="font-display text-base text-[#1F3A5F]">{b.title}</h3>
          <p className="text-sm text-neutral-700 mt-2 whitespace-pre-wrap leading-relaxed">
            {b.content}
          </p>
        </div>
      </div>
    );
  }
  return null;
}

function GuideBubble({ guide, text }) {
  const [typed, setTyped] = useState("");
  useEffect(() => {
    const reduceMotion = typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) { setTyped(text || ""); return; }
    setTyped("");
    let i = 0;
    const t = setInterval(() => {
      i += 1;
      setTyped((text || "").slice(0, i));
      if (i >= (text || "").length) clearInterval(t);
    }, 14);
    return () => clearInterval(t);
  }, [text]);
  const isDone = typed === (text || "");
  return (
    <div className="flex gap-3 items-start">
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#E5276A] to-[#1F3A5F] text-white flex items-center justify-center text-[10px] font-bold shrink-0 shadow-sm">
        gV·IA
      </div>
      <div className="flex-1 bg-white border border-[#1F3A5F]/10 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
        <div className="text-[10px] uppercase tracking-widest text-[#E5276A] font-semibold mb-1">
          {guide?.name || "geoVINUM IA"} · {guide?.role || "Formateur GeoVINUM"}
        </div>
        <p className="text-sm text-neutral-700 leading-relaxed whitespace-pre-wrap"
           dangerouslySetInnerHTML={{ __html: renderMarkdown(typed) }} />
        {!isDone && (
          <span className="inline-block w-1.5 h-3.5 bg-[#E5276A] ml-0.5 align-middle animate-pulse" />
        )}
      </div>
    </div>
  );
}

// Minimal Markdown bold support (**foo** → <strong>foo</strong>).
function formatBold(txt) {
  return renderMarkdown(txt);
}

function JourneyRecap({ journey, mastery, slug }) {
  const level = mastery?.level || "decouverte";
  const lvlMeta = LEVEL_LABELS[level];
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FDF9F3] via-white to-[#F5EDE2] py-8">
      <div className="max-w-2xl mx-auto px-4 text-center">
        <Trophy className="w-16 h-16 mx-auto mb-4" style={{ color: lvlMeta?.color }} />
        <div className="text-[11px] uppercase tracking-widest text-[#1F3A5F] font-semibold">
          Appellation {journey.appellation_name}
        </div>
        <h1 className="text-3xl md:text-4xl font-display text-[#1F3A5F] mt-2">
          {lvlMeta?.emoji} {lvlMeta?.label}
        </h1>
        {mastery && (
          <p className="text-sm text-neutral-600 mt-2">
            Score moyen : <span className="font-semibold">{mastery.average_score}/100</span>
          </p>
        )}
        {!mastery && (
          <p className="text-sm text-neutral-500 mt-3 italic">
            Connectez-vous pour sauvegarder votre tableau de maîtrise détaillé.
          </p>
        )}

        {mastery?.dimensions?.length > 0 && (
          <Card className="mt-8 text-left">
            <CardContent className="p-5">
              <h3 className="font-display text-lg text-[#1F3A5F] mb-3">
                Votre tableau de maîtrise
              </h3>
              <div className="space-y-2">
                {mastery.dimensions.map((d) => (
                  <div key={d.key} className="flex items-center gap-3" data-testid={`dim-${d.key}`}>
                    <span className="text-xl">{d.emoji}</span>
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-[#1F3A5F]">{d.label}</div>
                      <div className="h-1.5 bg-neutral-100 rounded-full mt-1 overflow-hidden">
                        <div className="h-full rounded-full"
                             style={{
                               width: `${d.score}%`,
                               background: d.status === "maitrise" ? "#10B981"
                                         : d.status === "compris" ? "#F59E0B"
                                         : d.status === "a_renforcer" ? "#EF4444"
                                         : "#D1D5DB",
                             }} />
                      </div>
                    </div>
                    <StatusPill status={d.status} />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="mt-8 flex flex-wrap justify-center gap-2">
          <Link to="/academy">
            <Button variant="outline">Autres voyages</Button>
          </Link>
          <Link to={`/courses/${slug}/classic`}>
            <Button variant="outline">
              <Award className="w-4 h-4 mr-1" /> Mode classique (quiz + certificat)
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    maitrise: { label: "Maîtrisé", color: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
    compris: { label: "Compris", color: "bg-amber-100 text-amber-700", Icon: CheckCircle2 },
    a_renforcer: { label: "À renforcer", color: "bg-rose-100 text-rose-700", Icon: AlertCircle },
    non_commence: { label: "À faire", color: "bg-neutral-100 text-neutral-500", Icon: Circle },
  };
  const m = map[status] || map.non_commence;
  const Ic = m.Icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium ${m.color}`}>
      <Ic className="w-3 h-3" /> {m.label}
    </span>
  );
}


/**
 * JourneyQuiz — end-of-journey mastery assessment.
 * Loads the backend-generated quiz, lets the learner answer each MCQ,
 * then submits for scoring. Shows per-question feedback with
 * explanations before unlocking the mastery recap.
 */
function JourneyQuiz({ slug, lang, level, appellationName, onDone }) {
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const load = () => {
    setLoading(true); setError(null);
    api.get(`/academy/courses/${slug}/journey/quiz`, {
      params: { lang, level },
    })
      .then((r) => {
        setQuestions(r.data?.questions || []);
      })
      .catch((e) => {
        setError(e?.response?.data?.detail || "Impossible de charger le quiz");
      })
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const allAnswered = questions.length > 0 && questions.every((q) => answers[q.id] !== undefined);

  const submit = async () => {
    if (!allAnswered) return;
    setSubmitting(true);
    try {
      const r = await api.post(`/academy/courses/${slug}/journey/quiz/submit`, {
        lang, level, answers,
      });
      setResult(r.data);
      setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "L'envoi du quiz a échoué");
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm text-neutral-500" data-testid="quiz-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> geoVINUM IA prépare votre évaluation…
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center flex-col gap-3 text-sm">
        <AlertCircle className="w-6 h-6 text-rose-500" />
        <p className="text-neutral-600">{error}</p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="quiz-retry">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Réessayer
          </Button>
          <Button onClick={onDone} data-testid="quiz-skip">
            Passer directement au tableau de maîtrise
          </Button>
        </div>
      </div>
    );
  }

  if (result) {
    const scoreColor = result.score >= 75 ? "#10B981"
                     : result.score >= 50 ? "#F59E0B" : "#EF4444";
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FDF9F3] via-white to-[#F5EDE2] py-8 pb-32">
        <div className="max-w-2xl mx-auto px-4">
          <div className="text-center">
            <Trophy className="w-14 h-14 mx-auto mb-3" style={{ color: scoreColor }} />
            <div className="text-[11px] uppercase tracking-widest text-[#1F3A5F] font-semibold">
              Quiz · {appellationName}
            </div>
            <h1 className="text-3xl md:text-4xl font-display text-[#1F3A5F] mt-2"
                data-testid="quiz-score">
              {result.correct} / {result.total}
            </h1>
            <p className="text-sm text-neutral-600 mt-1">
              Score global : <span className="font-semibold" style={{ color: scoreColor }}>{result.score}/100</span>
            </p>
          </div>
          <div className="mt-6 space-y-3" data-testid="quiz-results">
            {result.details.map((d, i) => (
              <Card key={d.id}
                    className={`border-l-4 ${d.is_correct ? "border-l-emerald-500" : "border-l-rose-500"}`}
                    data-testid={`quiz-result-${i}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-2">
                    {d.is_correct
                      ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-1" />
                      : <AlertCircle className="w-4 h-4 text-rose-500 shrink-0 mt-1" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">
                        {d.dimension}
                      </div>
                      <p className="text-sm text-[#1F3A5F] font-medium mt-0.5">{d.question}</p>
                      <ul className="mt-2 space-y-1 text-xs">
                        {d.options.map((opt, idx) => {
                          const isCorrect = idx === d.correct_index;
                          const isChosen = idx === d.chosen_index;
                          return (
                            <li key={idx}
                                className={`px-2 py-1 rounded ${
                                  isCorrect ? "bg-emerald-50 text-emerald-700 font-semibold"
                                  : isChosen ? "bg-rose-50 text-rose-700 line-through"
                                  : "text-neutral-500"
                                }`}>
                              {isCorrect ? "✓ " : isChosen ? "✗ " : "· "}{opt}
                            </li>
                          );
                        })}
                      </ul>
                      {d.explanation && (
                        <p className="text-xs text-neutral-600 mt-2 italic border-l-2 border-neutral-200 pl-2">
                          {d.explanation}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="mt-8 flex justify-center">
            <Button onClick={onDone}
                    data-testid="quiz-continue"
                    className="bg-gradient-to-r from-[#E5276A] to-[#1F3A5F] text-white">
              Voir mon tableau de maîtrise <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FDF9F3] via-white to-[#F5EDE2] py-8 pb-32">
      <div className="max-w-2xl mx-auto px-4">
        <div className="text-center mb-6">
          <div className="text-[11px] uppercase tracking-widest text-[#E5276A] font-semibold">
            Quiz final · {appellationName}
          </div>
          <h1 className="text-2xl md:text-3xl font-display text-[#1F3A5F] mt-1">
            Validez votre maîtrise
          </h1>
          <p className="text-sm text-neutral-600 mt-2">
            {questions.length} questions couvrant toutes les dimensions du voyage.
          </p>
        </div>
        <div className="space-y-4" data-testid="quiz-questions">
          {questions.map((q, qi) => (
            <Card key={q.id} data-testid={`quiz-q-${qi}`}>
              <CardContent className="p-4">
                <div className="text-[10px] uppercase tracking-widest text-[#1F3A5F]/60 font-semibold">
                  Question {qi + 1} · {q.dimension}
                </div>
                <p className="text-sm font-semibold text-[#1F3A5F] mt-1">{q.question}</p>
                <div className="mt-3 space-y-2">
                  {q.options.map((opt, oi) => {
                    const active = answers[q.id] === oi;
                    return (
                      <button key={oi}
                              type="button"
                              onClick={() => setAnswers((a) => ({ ...a, [q.id]: oi }))}
                              data-testid={`quiz-q-${qi}-opt-${oi}`}
                              className={`w-full text-left text-sm px-3 py-2 rounded-xl border transition ${
                                active
                                  ? "bg-[#E5276A] text-white border-[#E5276A]"
                                  : "bg-white text-[#1F3A5F] border-neutral-200 hover:border-[#E5276A]/50"
                              }`}>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="mt-8 flex items-center justify-between">
          <button onClick={onDone}
                  type="button"
                  data-testid="quiz-skip-later"
                  className="text-xs text-neutral-500 hover:text-[#E5276A] underline">
            Passer le quiz pour l'instant
          </button>
          <Button onClick={submit}
                  disabled={!allAnswered || submitting}
                  data-testid="quiz-submit"
                  className="bg-gradient-to-r from-[#E5276A] to-[#1F3A5F] text-white">
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Évaluation…</>
            ) : (
              <>Valider mon quiz <Sparkles className="w-4 h-4 ml-1" /></>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

